import React from 'react'; // , { useState, useEffect }
import { Navigate } from "react-router-dom";
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterSlot,
  CharacterResource,
  RollPlus,
  CharacterRoll,
  CharacterSpell,
  Attack,
  CharacterAction,
  CharacterAbility,
  CharacterWeapon,
  CharacterSheetInterface,
  BasicCreatureInstance,
  SummonInstance,
  Ability,
  CharacterFeature,
  SpellsFromList,
  ExtraEffectsFeature,
  ExtraEffectsOfTypeFeature,
  StoredSpell,
  CreatureAbility,
  AbilityEffect,
  ACTION_TYPES,
  SKILLS,
  SpellsFromListWithCosts,
} from "@dndModels";
import { DAMAGE_TYPES } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterResourceBoxes from "@dndComponents/character/misc/CharacterResourceBoxes";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import CharacterSkillPDF from "@dndComponents/character/pdf/CharacterSkillPDF";
import ImageFromString from "@dndComponents/display/ImageFromString";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterActionPDF from "@dndComponents/character/pdf/CharacterActionPDF";
import { isDamageType } from '@dndModels/Types';

interface Props { 
  character?: Character;
  char_int: CharacterSheetInterface | BasicCreatureInstance | SummonInstance;
  height: number;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterSheetPDF({
  character,
  char_int,
  height,
}: Props) {

  const renderBar1 = () => {
    let race_name = character && character.sidekick ? (character.sidekick_creature ? char_util.getUseName(character.sidekick_creature) : "No Creature") : (character?.race.source ? char_util.getUseName(character.race.source) : "No Race");
    if (race_name === "No Race" && character && character.character_options.races_called === "Species") {
      race_name = "No Species";
    }
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 6
          }}>
          <Grid
            className="centered"
            size={{
              xs: 8
            }}>
            <Grid container spacing={0} direction="row">
              <Grid
                style={{ marginLeft: "4px", fontSize: "16px" }}
                size={{
                  xs: 12
                }}>
                { char_int.name }
              </Grid>
              { char_int instanceof Character && char_int.classes.length > 1 && 
                <Grid
                  style={{ marginLeft: "4px", fontSize: "8px" }}
                  size={{
                    xs: 12
                  }}>
                  Character Level&nbsp;{ char_int.character_level }
                </Grid>
              }
              {/* { character && owner && 
                <Grid item xs={12} style={{ 
                    fontSize: "8px", 
                    color: "gray",
                  }}>
                  { owner.username }
                </Grid>
              } */}
            </Grid>
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            { character && 
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <div>
                    { character.class_string }
                  </div>
                  <div style={{ 
                      fontSize: "8px", 
                      color: "gray",
                    }}>
                    { character.classes.length === 1 ? 
                      <span>Class</span>
                      :
                      <span>Classes</span>
                    }
                  </div>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <div>
                    { race_name }
                  </div>
                  <div style={{ 
                      fontSize: "8px", 
                      color: "gray",
                    }}>
                    { character.character_options.races_called }
                  </div>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <div>
                    { character.background.source ? ` ${char_util.getUseName(character.background)}` : " No Background" } 
                  </div>
                  <div style={{ 
                      fontSize: "8px", 
                      color: "gray",
                    }}>
                    Background
                  </div>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  { (character.gifts.length > 0 || character.extra_feats.length > 0) &&
                    <>
                      <div>
                        { renderGifts() }
                      </div>
                      <div style={{ 
                          fontSize: "8px", 
                          color: "gray",
                        }}>
                        Extra Feats &amp; Gifts
                      </div>
                    </>
                  }
                </Grid>
              </>
            }
          </Grid>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 6
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            <div style={{ float: "right", marginRight: "4px" }}>
              <Grid container spacing={0} direction="row" style={{ border: "1px solid" }}>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: char_int instanceof Character ? 5 : 8
                  }}>
                  <Grid
                    className="centered"
                    style={{ height: "15px" }}
                    size={{
                      xs: 12
                    }}>Hit Points</Grid>
                  <Grid
                    className="centered padded"
                    style={{ height: "calc(100% - 25px)" }}
                    size={{
                      xs: 4
                    }}>
                    <div className="centered" style={{ backgroundColor: "lightgray", height: "100%", width: "100%", fontSize: "16px", color: "#848181" }}>
                      { char_int.hp.max }
                    </div>
                  </Grid>
                  <Grid
                    className="padded"
                    style={{ height: "calc(100% - 25px)" }}
                    size={{
                      xs: 4
                    }}>
                    <div style={{ backgroundColor: "lightgray", height: "100%", width: "100%" }}>
                      &nbsp;
                    </div>
                  </Grid>
                  <Grid
                    className="padded"
                    style={{ height: "calc(100% - 25px)" }}
                    size={{
                      xs: 4
                    }}>
                    <div style={{ backgroundColor: "lightgray", height: "100%", width: "100%" }}>
                      &nbsp;
                    </div>
                  </Grid>
                  <Grid
                    className="centered"
                    style={{ fontSize: "8px", height: "10px" }}
                    size={{
                      xs: 4
                    }}>
                    Max
                  </Grid>
                  <Grid
                    className="centered"
                    style={{ fontSize: "8px", height: "10px" }}
                    size={{
                      xs: 4
                    }}>
                    Current
                  </Grid>
                  <Grid
                    className="centered"
                    style={{ fontSize: "8px", height: "10px" }}
                    size={{
                      xs: 4
                    }}>
                    Temp
                  </Grid>
                </Grid>
                { (char_int instanceof Character) &&
                  <Grid
                    container
                    spacing={0}
                    direction="row"
                    style={{ borderLeft: "1px solid" }}
                    size={{
                      xs: 5
                    }}>
                    { char_int instanceof Character &&
                      <>
                        <Grid
                          className="centered"
                          style={{ height: "15px" }}
                          size={{
                            xs: 6
                          }}>Hit Dice</Grid>
                        <Grid
                          className="centered"
                          style={{ height: "15px" }}
                          size={{
                            xs: 6
                          }}>Death Saves</Grid>
                        <Grid
                          container
                          spacing={0}
                          direction="row"
                          size={{
                            xs: 6
                          }}>
                          <Grid
                            style={{ fontSize: "8px", height: "10px" }}
                            size={{
                              xs: 12
                            }}>Total: { char_int.hit_dice_string() }</Grid>
                          <Grid
                            style={{ padding: "4px", height: "calc(100% - 10px)" }}
                            size={{
                              xs: 12
                            }}>
                            <div style={{ backgroundColor: "lightgray", height: "100%", width: "100%" }}>
                              &nbsp;
                            </div>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          spacing={0}
                          direction="row"
                          size={{
                            xs: 6
                          }}>
                          <Grid
                            className="centered"
                            style={{ fontSize: "8px" }}
                            size={{
                              xs: 12
                            }}>Successes</Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                          <Grid
                            className="centered"
                            style={{ fontSize: "8px" }}
                            size={{
                              xs: 12
                            }}>Failures</Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 4
                            }}>
                            <div className="centered"><ImageFromString image="Circle" /></div>
                          </Grid>
                        </Grid>
                      </>
                    }
                  </Grid>
                }
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  style={{ borderLeft: "1px solid" }}
                  size={{
                    xs: char_int instanceof Character ? 2 : 4
                  }}>
                  <Grid
                    className="centered"
                    style={{ height: "15px" }}
                    size={{
                      xs: 12
                    }}>
                    Inspiration
                  </Grid>
                  <Grid
                    style={{ padding: "4px", height: "calc(100% - 15px)" }}
                    size={{
                      xs: 12
                    }}>
                    <div style={{ backgroundColor: "lightgray", height: "100%", width: "100%" }}>
                      &nbsp;
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  const renderGifts = () => {
    if (char_int instanceof Character) {
      if (char_int.gifts.length === 0 && char_int.extra_feats.length === 0) { 
        return "No Gifts or Extra Feats";
      } else {
        const gifts = char_int.gifts.map(o => o.name).join(", ");
        const extra_feats = char_int.extra_feats.map(o => o.name).join(", ");
        return gifts.length > 0 && extra_feats.length > 0 ? gifts + ", " + extra_feats : gifts + extra_feats;
      }
    } else {
      return "";
    }
  }

  const renderBar2 = () => {
    let modifier = char_int.initiative_modifier;
    if (!(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) && char_int.jack_of_all_trades) {
      modifier += Math.floor(char_int.proficiency_modifier * 0.5);
    }
    return (
      <>
        <Grid
          container
          spacing={0}
          direction="row"
          style={{ 
            borderTop: "1px solid", 
            fontSize: "16px" 
          }}
          size={{
            xs: 7
          }}>
          <Grid
            size={{
              xs: 3
            }}>
            <Grid container spacing={0} direction="column">
              <Grid
                className="centered"
                style={{
                  fontSize: "8px",
                }}>
                Size
              </Grid>
              <Grid className="centered">
                { typeof char_int.use_size === "string" ? char_int.use_size : common_util.size_map[char_int.use_size] }
              </Grid>
              <Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            size={{
              xs: 9
            }}>
            <Grid container spacing={0} direction="row">
              <Grid
                container
                spacing={0}
                direction="column"
                size={{
                  xs: 3
                }}>
                <Grid
                  className="centered"
                  style={{
                    fontSize: "8px",
                  }}>
                  Proficiency Bonus
                </Grid>
                <Grid className="centered">
                  { `+${char_int.proficiency_modifier}` }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <Grid container spacing={0} direction="column">
                  <Grid
                    className="centered"
                    style={{
                      fontSize: "8px",
                    }}>
                    Speed
                  </Grid>
                  <Grid className="centered">
                    { char_int.speed.walk } ft
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={0}
                direction="column"
                size={{
                  xs: 3
                }}>
                <Grid
                  className="centered"
                  style={{
                    fontSize: "8px",
                  }}>
                  Initiative
                </Grid>
                <Grid className="centered">
                  { common_util.addPlusMaybe(modifier) }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 3
                }}>
                <Grid container spacing={0} direction="column">
                  <Grid
                    className="centered"
                    style={{
                      fontSize: "8px",
                    }}>
                    Armor Class
                  </Grid>
                  <Grid className="centered">
                    { char_int.armor_class }
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          style={{ borderTop: "1px solid" }}
          size={{
            xs: 5
          }}>
          { renderSenses() }
        </Grid>
      </>
    );
  }

  const renderSenses = () => {
    let passive_perception = 10; // char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance ? char_int.passives.passive_perception : 10 + char_int.passive_bonuses.perception;
    let passive_investigation = 10; // char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance ? char_int.passives.passive_investigation : 10 + char_int.passive_bonuses.investigation;
    let passive_insight = 10; // char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance ? char_int.passives.passive_insight : 10 + char_int.passive_bonuses.insight;
    let skill_finder = SKILLS.filter(o => o.name === "Perception");
    if (!(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) && skill_finder.length === 1) {
      const skill = skill_finder[0];
      const { modifier } = char_util.skillModifier(char_int, skill);
      if (modifier) {
        passive_perception += modifier;
      }
    }
    skill_finder = SKILLS.filter(o => o.name === "Investigation");
    if (!(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) && skill_finder.length === 1) {
      const skill = skill_finder[0];
      const { modifier } = char_util.skillModifier(char_int, skill);
      if (modifier) {
        passive_investigation += modifier;
      }
    }
    skill_finder = SKILLS.filter(o => o.name === "Insight");
    if (!(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) && skill_finder.length === 1) {
      const skill = skill_finder[0];
      const { modifier } = char_util.skillModifier(char_int, skill);
      if (modifier) {
        passive_insight += modifier;
      }
    }
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <div style={{ float: "left", lineHeight: "1.6" }}>
            Passives
          </div>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <div style={{ float: "left", lineHeight: "1.6" }}>
            Perception: { passive_perception }&nbsp;
            Investigation: { passive_investigation }&nbsp;
            Insight: { passive_insight }
          </div>
        </Grid>
        { (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) ? 
          <Grid
            size={{
              xs: 12
            }}>{ char_int.senses_string }</Grid>
        : char_int.senses.map((sense, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <b><DisplayObjects type="sense" ids={[sense.sense_id]} />:</b> Out to { sense.range } feet
                          </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderBar3 = () => {
    if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
      return (
        <Grid
          style={{ borderTop: "1px solid", borderBottom: "1px solid" }}
          size={{
            xs: 12
          }}>
          <div
            style={{
              display: "flex",
              // minHeight: "76px",
              overflowX: "auto" 
            }}>
            {/* { Object.keys(char_int.slots).map((level, key) => {
              return (
                <span key={key}>
                  <CharacterResourceBoxes
                    label={ `${level}` }
                    used={0}
                    total={char_int.slots[+level]}
                    true_total={char_int.slots[+level]}
                    created={0}
                    type="Slots"
                    show_type={false}
                    character={character}
                    // threshold={0}
                    flat
                    onChange={(used: number) => {
                    }}
                  />
                </span>
              );
            })} */}
            { char_int.legendary_actions_per_round > 0 &&
              <CharacterResourceBoxes
                label="Legendary Actions"
                used={0}
                total={char_int.legendary_actions_per_round}
                true_total={char_int.legendary_actions_per_round}
                created={0}
                show_type={false}
                character={character}
                // threshold={0}
                flat
                shared
                onChange={(used: number) => {
                }}
              />
            }
            { char_int.legendary_resistances > 0 &&
              <CharacterResourceBoxes
                label="Legendary Resistances"
                used={0}
                total={char_int.legendary_resistances}
                true_total={char_int.legendary_resistances}
                created={0}
                show_type={false}
                character={character}
                // threshold={0}
                flat
                shared
                onChange={(used: number) => {
                }}
              />
            }
          </div>
        </Grid>
      );
    } else {
      const slot_types = Array.from(new Set(char_int.use_slots.map(o => o.slot_name)));
      // debugger;
      return (
        <Grid
          style={{ borderTop: "1px solid", borderBottom: "1px solid" }}
          size={{
            xs: 12
          }}>
          <div
            style={{
              display: "flex",
              // minHeight: "76px",
              overflowX: "auto" 
            }}>
            { slot_types.map(slot_name => {
              return renderSlotsForType(slot_name);
            })}
            { char_int.resources.map(resource => {
              return renderResource(resource);
            })}
          </div>
        </Grid>
      );
    }
  }

  const renderSlotsForType = (slot_name: string) => {
    if (!(char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance)) {
      const slots = char_int.use_slots.filter(o => o.slot_name === slot_name);
      const groups: any = [];
      let group: any = [];
      // debugger;
      for (let i = 0; i < slots.length; ++i) {
        // const width = slots.length < 4 ? 12 : slots.length < 7 ? 6 : (i < 4 ? 5 : i < 7 ? 4 : 3);
        const slot = slots[i];
        group.push(renderSlot(slot));
        if (i % 3 === 2 || i === slots.length - 1) {
          groups.push(
            <span key={i}>
              { group }
            </span>
          );
          group = [];
        }
      }
      return (
        <span key={slot_name} style={{ 
          borderRight: "1px solid",
          minWidth: "140px"
        }}>
          <Grid container spacing={0} direction="row">
            <Grid
              style={{
                fontWeight: "bold"
              }}
              size={{
                xs: 12
              }}>
              &nbsp;{ slot_name === "Slots" ? "Spell Slots" : (slot_name === "Pact" ? "Pact Slots" : slot_name) }
            </Grid>
            <Grid
              style={{ fontSize: "14px" }}
              size={{
                xs: 12
              }}>
              { groups }
            </Grid>
          </Grid>
        </span>
      );
    }
    return null;
  }

  const renderSlot = (slot: CharacterSlot) => {
    return (
      <CharacterResourceBoxes key={slot.level}
        label={ `${slot.level}` }
        used={0}
        total={slot.total}
        true_total={slot.true_total}
        created={0}
        type={slot.slot_name}
        show_type={false}
        character={character}
        // threshold={0}
        flat
        shared
        onChange={(used: number) => {
        }}
      />
    );
  }

  const renderResource = (resource: CharacterResource) => {
    return (
      <span key={`${resource.type_id}_${resource.class_id}`} style={{ 
        borderRight: "1px solid",
        minWidth: "140px"
      }}>
        <Grid container spacing={0} direction="column">
          <Grid
            style={{
              fontWeight: "bold"
            }}>
            { resource.name }&nbsp;
            { resource.size > 1 && ` (d${resource.size})` }&nbsp;
          </Grid>
          { resource.show_source_name && 
            <Grid
              style={{
                fontWeight: "bold"
              }}>
              ({ resource.source_name })
            </Grid>
          }
          <Grid>
            <CharacterResourceBoxes  
              used={resource.used}
              total={resource.total}
              true_total={resource.true_total}
              created={0}
              character={character}
              shared
              onChange={(used: number) => {
              }}
            />
          </Grid>
        </Grid>
      </span>
    );
  }

  const renderAbilityScores = () => {
    return (
      <Grid container spacing={0} direction="row" className="curved_border">
        <Grid
          className="centered"
          style={{ fontSize: "12px" }}
          size={{
            xs: 12
          }}>Ability Scores</Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 2
          }}>
          <Grid
            size={{
              xs: 12
            }}>&nbsp;{/* Name */}</Grid>
          <Grid
            size={{
              xs: 12
            }}>Modifier</Grid>
          <Grid
            size={{
              xs: 12
            }}>Saving Throw</Grid>
          {/* <Grid item xs={12}>Score</Grid> */}
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 10
          }}>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Strength") }</Grid>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Dexterity") }</Grid>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Constitution") }</Grid>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Intelligence") }</Grid>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Wisdom") }</Grid>
          <Grid
            size={{
              xs: 2
            }}>{ renderAbility("Charisma") }</Grid>
        </Grid>
      </Grid>
    );
  }

  const renderAbility = (ability: string) => {
    const modifier = char_int.current_ability_scores.getModifier(ability);
    const score = char_int.current_ability_scores.getAbilityScore(ability);
    if (modifier !== null && score !== null) {
      return (
        <Grid
          key={ability}
          className="centered"
          size={{
            xs: 12
          }}>
          <div style={{
              width: "95px"
            }}>
            <Grid container spacing={0} direction="column" className="curved_border">
              <Grid>
                <div className="centered" style={{
                  fontSize: "11px",
                  fontWeight: "bold",
                }}>
                  { ability }
                </div>
              </Grid>
              <Grid style={{ fontSize: "16px" }}>
                { common_util.addPlusMaybe(modifier) }
              </Grid>
              <Grid className="centered">
                <div className="centered" style={{
                  width: "35px",
                  fontSize: "8px",
                  fontWeight: "bold",
                }}>
                  { renderSavingThrow(ability) }
                </div>
              </Grid>
              {/* <Grid item className="centered">
                <div className="curved_border centered" style={{
                  width: "35px",
                  fontSize: "8px",
                  fontWeight: "bold",
                }}>
                  { score }
                </div>
              </Grid> */}
            </Grid>
          </div>
        </Grid>
      );
    }
    return null;
  }

  const renderSavingThrow = (ability: string) => {
    const abbrev = common_util.ability_score_abbreviation(ability);
    // const full_name = common_util.ability_score_full(ability);
    if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
      return common_util.addPlusMaybe(char_int.get_saving_throw(abbrev));
    } else {
      const char_roll = char_int.saving_throws[abbrev] as CharacterRoll;
      const roll_plus = new RollPlus();
      roll_plus.flat = char_roll.modifier;
      let bonuses_string = "";
      let modifier = char_roll.modifier;
      char_roll.rolls.forEach(bonus => {
        const roll_plus2 = bonus;
        if (roll_plus2) {
          modifier += roll_plus2.flat;
          if (!["","None","undefined"].includes(roll_plus2.ability_score)) {
            const mod = char_int.current_ability_scores.getModifier(roll_plus2.ability_score);
            if (mod) {
              modifier += mod;
            }
          }
          if (roll_plus2.size === 1) {
            modifier += roll_plus2.count;
          } else if (roll_plus2.size > 1 && roll_plus2.count !== 0) {
            roll_plus2.recalculate_string();
            if (roll_plus2.as_string.length > 0 && roll_plus2.as_string[0] !== "-") {
              bonuses_string += `+${roll_plus2.as_string}`;
            } else {
              bonuses_string += roll_plus2.as_string;
            }
          }
        }
      });
      if (modifier > 0) {
        bonuses_string = `+${modifier}${bonuses_string}`;
      } else if (modifier < 0) {
        bonuses_string = `${modifier}${bonuses_string}`;
      } else if (bonuses_string === "") {
        bonuses_string = "+0";
      }
      
      return `${ char_roll.proficiency === 1 ? "P" : "" } ${ bonuses_string }`;
    }
  }

  const renderSkills = () => {
    if (char_int instanceof BasicCreatureInstance) {
      if (char_int.skills_string.length > 0) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <b>Skills</b> { char_int.skills_string }
          </Grid>
        );
      }
      return null;
    } else if (char_int instanceof SummonInstance) {
      return (<>{ char_int.skills_string }</>); // This is just temporary. I need to overhaul this control.
      // const skills = Object.keys(char_int.skill_proficiencies);
      // return skills.sort((a,b) => { return a.localeCompare(b); }).map((skill, key) => {
      //   return (
      //     <Grid
      //       key={key}
      //       size={{
      //         xs: 4
      //       }}>
      //       { skill }: { common_util.addPlusMaybe(char_int.skill_proficiencies[skill]) }
      //     </Grid>
      //   );
      // });
    } else {
      const skills = SKILLS;
      return skills.sort((a,b) => { return a.name.localeCompare(b.name); }).map((skill, key) => {
        return (
          <Grid
            key={key}
            size={{
              xs: 4
            }}>
            <CharacterSkillPDF
              char_int={char_int}
              skill={skill}
            />
          </Grid>
        );
      });
    }
  }

  const renderTools = () => {
    if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
      const tools = Object.keys(char_int.tool_proficiencies);
      if (tools.length > 0) {
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            className="centered curved_border"
            size={{
              xs: 12
            }}>
            <Grid
              style={{ fontSize: "12px" }}
              size={{
                xs: 12
              }}>Tools</Grid>
            { tools.sort((a,b) => { return a.localeCompare(b); }).map((tool, key) => {
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 4
                  }}>
                  { tool }
                </Grid>
              );
            })}
          </Grid>
        );
      }
    } else {
      if (char_int.tool_proficiencies.length > 0) {
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            className="centered curved_border"
            size={{
              xs: 12
            }}>
            <Grid
              style={{ fontSize: "12px" }}
              size={{
                xs: 12
              }}>Tools</Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayObjects type="tool" ids={ char_int.tool_proficiencies.map(o => o.proficiency_id) } />
            </Grid>
          </Grid>
        );
      }
    }
    return null;
  }

  const renderLanguages = () => {
    if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
      if (char_int.languages.length > 0) {
        return (
          <Grid
            container
            spacing={0}
            direction="row"
            className="centered curved_border"
            size={{
              xs: 12
            }}>
            <Grid
              style={{ fontSize: "12px" }}
              size={{
                xs: 12
              }}>Languages</Grid>
            <Grid
              size={{
                xs: 12
              }}>
              { char_int.languages }
            </Grid>
          </Grid>
        );
      }
    } else {
      return (
        <Grid
          container
          spacing={0}
          direction="row"
          className="centered curved_border"
          size={{
            xs: 12
          }}>
          <Grid
            style={{ fontSize: "12px" }}
            size={{
              xs: 12
            }}>Languages</Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <DisplayObjects type="language" ids={ char_int.languages_known } />
          </Grid>
        </Grid>
      );
    }
    return null;
  }

  const getActions = (passives = false) => {
    let actions: CharacterAction[] = [];
    if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
      // debugger;
      // console.error("renderActions not implemented for creatures");
      const makeAction = (o: CharacterFeature, casting_time: "Special" | "A" | "Legendary Action" | "Lair Action") => {
        const char_ability = new CharacterAbility();
        char_ability.copyFeature(o, char_int, character);
        const action = new CharacterAction();
        action.type = "Ability";
        if (char_ability.the_ability && 
          char_ability.the_ability instanceof Ability && 
          char_ability.the_ability.special_action_type !== "Inherit") {
          action.special_action_type = char_ability.the_ability.special_action_type;
        } else if (o.feature_type === "Extra Attacks" || o.feature_type === "Minion Extra Attacks") {
          char_int.extra_attacks_string = o.floating_feature?.description??"";
        } else if (o.feature_type === "") {
          char_ability.name = o.floating_feature?.name??"";
          action.special_action_type = "Ability";
        } else {
          // char_ability._name = o.floating_feature?.name??"";
          action.special_action_type = "Ability";
        }
        action.action = char_ability;
        action.lowest = true;
        action.casting_time = casting_time;
        return action;
      }
      actions = [
        ...char_int.traits.map(o => {
          return makeAction(o, "Special");
        }),...char_int.actions.map(o => {
          return makeAction(o, "A");
        }),...char_int.legendary_actions.map(o => {
          return makeAction(o, "Legendary Action");
        }),...char_int.lair_actions.map(o => {
          return makeAction(o, "Lair Action");
        })
      ];
      // actions = actions.filter(o => o.name !== "");
    } else {
      actions = char_int.actions.filter(o => 
        !o.hide_in_pdf && !o.hidden &&
        o.action && 
        o.is_passive === passives && 
        validAction(o)
      ).sort((a: any, b: any) => { return a.name.localeCompare(b.name) });
    }
    return actions;
  }

  const validAction = (o: CharacterAction) => {
    if (character instanceof Character) {
      if (o.action instanceof Attack) {
        return !character.unarmed_strike_customizations.hidden;
      } else {
        if (o.action instanceof CharacterSpell) {
          if (o.action.hidden) {
            return false;
          } else if (!o.lowest) {
            return o.action.show_upcasts.includes(o.level);
          } else if (o.ritual_only && o.action.hide_ritual) {
            return false;
          // } else if (o.action.ritual && !show_rituals) {
          //   return false;
          } else {
            return true;
          }
        } else if (o.action instanceof StoredSpell) {
          if (o.action.hidden) {
            return false;
          } else {
            return true;
          }
        } else if (o.action instanceof CharacterAbility) {
          // if (o.action.ritual && !show_rituals) {
          //   return false;
          // } else 
          if (o.action.the_ability instanceof SpellsFromList || 
            o.action.the_ability instanceof SpellsFromListWithCosts) {
            if (o.special_resource) {
              return !o.action.hide_special;
            } else if (!o.lowest) {
              return o.action.show_upcasts.includes(o.level);
            } else if (o.ritual_only && o.action.hide_ritual) {
              return false;
            } else if (o.action.hidden) {
              return false;
            } else {
              return true;
            }
          } else if (o.action.the_ability instanceof Ability) {
            if (o.special_resource) {
              return !o.action.hide_special;
            } else if (o.action.hidden) {
              return false;
            } else {
              return true;
            }
          } else if (o.action.hidden) {
            return false;
          } else if (o.action.the_ability instanceof CreatureAbility) {
            return true;
          } else {
            console.log(o);
            return true;
          }
        } else if (o.action instanceof CharacterWeapon) {
          return true;
        } else {
          console.log(o);
          return true;
        }
      }
    }
    return false;
  }

  const renderActions = () => {
    const actions = getActions();
    const attacks = actions.filter(o => 
      (o.casting_time === "A" || o.casting_time === "Attack" || o.casting_time === "Light Attack") &&
      (o.action instanceof CharacterWeapon || o.action instanceof Attack));
    const As = actions.filter(o => 
        o.casting_time === "A" && !o.ritual_only &&
        !(o.action instanceof CharacterWeapon || o.action instanceof Attack));
    // debugger;
    const BAs = actions.filter(o => o.casting_time === "BA");
    const RAs = actions.filter(o => o.casting_time === "RA");
    const legendary = actions.filter(o => o.casting_time === "Legendary Action");
    const lair = actions.filter(o => o.casting_time === "Lair Action");
    const others = actions.filter(o => 
      o.ritual_only || !["A","BA","RA","Attack","Light Attack"].includes(o.casting_time));
    return (
      <Grid
        size={{
          xs: 12
        }}>
        { renderActionsForCastingTime(attacks, "Attack") }
        { renderActionsForCastingTime(As, "A") }
        { renderActionsForCastingTime(BAs, "BA") }
        { renderActionsForCastingTime(legendary, "Legendary Action") }
        { renderActionsForCastingTime(lair, "Lair Action") }
        { renderActionsForCastingTime(RAs, "RA") }
        { renderActionsForCastingTime(others, "Other") }
      </Grid>
    );
  }

  const renderActionsForCastingTime = (actions: CharacterAction[], casting_time: string) => {
    const return_us: any[] = [];
    if (actions.length > 0) {
      try {
        switch (casting_time) {
          case "Attack": 
            let extra_attacks = "";
            if (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) {
              extra_attacks = char_int.extra_attacks_string;
            } else if (char_int.extra_attacks > 0) {
              extra_attacks = `${char_int.extra_attacks + 1} Attacks per Action`;
            }
            return_us.push(
              <Grid key="Attacks" style={{ fontWeight: "bold" }}>
                &nbsp;Attacks { extra_attacks.length > 0 && <span style={{ color: "gray"}}>- { extra_attacks }</span> }
              </Grid>
            );
          break;
          case "A":
            return_us.push(
              <Grid key="A" style={{ fontWeight: "bold" }}>
                &nbsp;Actions { (char_int instanceof BasicCreatureInstance || char_int instanceof SummonInstance) && char_int.extra_attacks_string.length > 0 && <span style={{ color: "gray"}}>- { char_int.extra_attacks_string }</span> }
              </Grid>
            );
          break;
          case "BA":
            return_us.push(
              <Grid key="BA" style={{ fontWeight: "bold" }}>
                &nbsp;Bonus Actions
              </Grid>
            );
          break;
          case "Legendary Action":
            return_us.push(
              <Grid key="Legendary Action" style={{ fontWeight: "bold" }}>
                &nbsp;Legendary Actions
              </Grid>
            );
          break;
          case "Lair Action":
            return_us.push(
              <Grid key="Lair Action" style={{ fontWeight: "bold" }}>
                &nbsp;Lair Actions
              </Grid>
            );
          break;
          case "RA":
            return_us.push(
              <Grid key="RA" style={{ fontWeight: "bold" }}>
                &nbsp;Reactions
              </Grid>
            );
          break;
          case "Other":
            return_us.push(
              <Grid key="Other" style={{ fontWeight: "bold" }}>
                &nbsp;Other
              </Grid>
            );
          break;
          case "Passive":
            return_us.push(
              <Grid key="Passive" style={{ fontWeight: "bold" }}>
                &nbsp;Passive Effects
              </Grid>
            );
          break;
        }
        const dingen = actions.filter(o => o.special_action_type !== "None");
        if (dingen && dingen.length > 0) {
          const weapons = dingen.filter(o => o.type === "Weapon");
          const abilities = dingen.filter(o => o.type === "Ability" && o.lowest);
          const spells = dingen.filter(o => o.type !== "Weapon" && o.type !== "Ability");

          return_us.push(
            <Grid key={`specials`} container spacing={0} direction="row">
              { weapons.map(action => {
                return (
                  <Grid
                    key={action.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CharacterActionPDF
                      character={character}
                      char_int={char_int}
                      action={action} 
                    />
                  </Grid>
                );
              })}
              { abilities.map(action => {
                return (
                  <Grid
                    key={action.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CharacterActionPDF
                      character={character}
                      char_int={char_int}
                      action={action} 
                    />
                  </Grid>
                );
              })}
              { spells.map(action => {
                return (
                  <Grid
                    key={action.true_id}
                    size={{
                      xs: 12
                    }}>
                    <CharacterActionPDF
                      character={character}
                      char_int={char_int}
                      action={action} 
                    />
                  </Grid>
                );
              })}
            </Grid>
          );
        }
      } catch (ex) {
        console.error(ex);
        console.error(ACTION_TYPES);
      }
    }
    return return_us;
  }

  const renderNotes = () => {
    if (char_int instanceof Character) {
      const notes_cfss = char_int.all_char_feature_sources.filter(o => o.notes.length > 0);
      if (notes_cfss.length > 0) {
        return (
          <Grid
            size={{
              xs: 12
            }}>
            <div style={{ padding: "4px", paddingRight: "8px" }}>
              { notes_cfss.map((cfs, key) => {
                return (
                  <Grid key={key} container spacing={0} direction="row">
                    <Grid
                      className="header"
                      size={{
                        xs: 12
                      }}>{ char_util.getUseName(cfs) }</Grid>
                    <Grid
                      size={{
                        xs: 12
                      }}>
                      <DisplayStringWithReplacements
                        display_string={cfs.notes}
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </div>
          </Grid>
        );
      }
    }
    return null;
  }

  const renderExtraEffects = () => {
    if (character && character.extra_effects.length > 0) {
      let attack_mod = 0;
      const actions = getActions();
      const attacks = actions.filter(o => 
        (o.casting_time === "A" || o.casting_time === "Attack") &&
        (o.action instanceof CharacterWeapon || o.action instanceof Attack)
      );
      // console.log(attacks); 
      if (attacks.length > 0) {
        for (const attack of attacks) {
          if (attack.action instanceof CharacterWeapon) {
            for (const a of attack.action.attacks) {
              let am = +a.attack_string.replace("+","");
              am -= char_int.proficiency_modifier;
              if (am > attack_mod) {
                attack_mod = am;
              }
            }
          } else if (attack.action instanceof Attack) {
            let am = +attack.action.attack_string.replace("+","");
            am -= char_int.proficiency_modifier;
            if (am > attack_mod) {
              attack_mod = am;
            }
          }
        }
      }
      return (
        <>
          <Grid
            className='header padded'
            size={{
              xs: 12
            }}>Extra Effects</Grid>
          { character.extra_effects.filter(o => o.apply_to.length > 1 || !o.apply_to.includes("Abilities") || o.ability_limit.name.length === 0).sort((a,b) => {
            if (a.main_cf.true_id === b.main_cf.true_id) {
              return a.name.localeCompare(b.name);
            } else {
              return a.main_cf.name.localeCompare(b.main_cf.name);
            }
          }).map(cee => {
            // console.log(cee);
            return (
              <Grid
                key={cee.true_id}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row" style={{ backgroundColor: "lightgray" }}>
                  <Grid
                    className="centered"
                    size={{
                      xs: 3
                    }}>
                    { cee.name }
                  </Grid>
                  <Grid
                    size={{
                      xs: 9
                    }}>
                    <Grid container spacing={0} direction="row">
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        { cee.resource_consumed !== "None" && cee.amount_consumed > 0 &&
                          <>
                            { cee.amount_consumed }&nbsp;
                            { cee.resource_consumed === "Slot" ? 
                              <>Level { cee.slot_level } Slot</>
                            : 
                              <DisplayObjects type='resource' ids={[cee.resource_consumed]} />
                            }
                          </>
                        }
                      </Grid>
                      <Grid
                        size={{
                          xs: 9
                        }}>
                        <Grid container spacing={0} direction="row">
                          { cee.effect_cfs.map(ee_cf => {
                            return (
                              <Grid
                                key={ee_cf.true_id}
                                size={{
                                  xs: 12
                                }}>
                                <Grid container spacing={0} direction="row">
                                  { (ee_cf.floating_feature instanceof ExtraEffectsFeature || 
                                    ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) && 
                                    ee_cf.floating_feature.effects.map(effect => {
                                    if (effect.type === "Damage") {
                                      // console.log(effect);
                                      if (ee_cf.floating_feature instanceof ExtraEffectsFeature || ee_cf.floating_feature instanceof ExtraEffectsOfTypeFeature) {
                                        const potence_string = char_util.getPotenceString({ 
                                          slot_level: 1, 
                                          char: character, 
                                          char_int: character, 
                                          spell: ee_cf, 
                                          item_id: ee_cf.item_id, 
                                          effect, 
                                          cfs_id: (ee_cf ? ee_cf.source_id : ""), 
                                          attack_mod, 
                                          // action 
                                        });
                                        // console.log(potence_string);
                                        return (
                                          <Grid
                                            key={effect.true_id}
                                            size={{
                                              xs: 12
                                            }}>
                                            <Grid container spacing={0} direction="row">
                                              <Grid
                                                size={{
                                                  xs: 5
                                                }}></Grid>
                                              <Grid
                                                size={{
                                                  xs: 3
                                                }}></Grid>
                                              <Grid
                                                className="centered"
                                                size={{
                                                  xs: 4
                                                }}>
                                                { potence_string }&nbsp;{ renderEffectType(effect) }
                                              </Grid>
                                            </Grid>
                                          </Grid>
                                        );
                                      }
                                    } else if (effect.type === "Control" || effect.type === "Utility") {
                                      return (
                                        <Grid
                                          key={effect.true_id}
                                          size={{
                                            xs: 12
                                          }}>
                                          <Grid container spacing={0} direction="row">
                                            <Grid
                                              size={{
                                                xs: 5
                                              }}></Grid>
                                            <Grid
                                              size={{
                                                xs: 3
                                              }}></Grid>
                                            <Grid
                                              className="centered"
                                              size={{
                                                xs: 4
                                              }}>
                                              { effect.utility_detail }
                                            </Grid>
                                          </Grid>
                                        </Grid>
                                      );
                                    } else {
                                      console.log(effect.type);
                                    }
                                    return null;
                                  })}
                                </Grid>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </>
      );
    }
    return null;
  }

  const renderEffectType = (effect: AbilityEffect) => {
    const effect_type = effect.type;
    if (isDamageType(effect_type)) {
      return (
        <ImageFromString image={effect_type} />
      );
    } else if (effect_type === "Damage") {
        return effect.damage_types.map((dt, key) => {
          return <span key={key}><ImageFromString image={dt} />&nbsp;</span>;
        });
    } else if (effect_type === "Healing" || effect_type === "Temp HP" || effect_type === "Max HP") {
      return (
        <ImageFromString image={effect_type} />
      );
    }
    return null;
  }

  const renderPassives = () => {
    const actions = getActions(true);
    return (
      <Grid
        size={{
          xs: 12
        }}>
        { renderActionsForCastingTime(actions, "Passive") }
      </Grid>
    );
  }

  if (api.user === null) {
    return <Navigate to="/" />;
  } else { 
    return (
      <div style={{ 
        fontWeight: "bold", 
        fontSize: "10px", 
        lineHeight: "1.5", 
        border: "1px solid",
        margin: "10px" 
      }}>
        <Grid container spacing={0} direction="row">
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            { renderBar1() }
            { renderBar2() }
            { renderBar3() }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 12
            }}>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 12
                }}>
                <div>
                  { renderAbilityScores() }
                </div>
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <Grid container spacing={0} direction="row" className="curved_border">
                  <Grid
                    className="centered"
                    style={{ fontSize: "12px" }}
                    size={{
                      xs: 12
                    }}>Skills</Grid>
                  { renderSkills() }
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <div>
                  <Grid container spacing={0} direction="row">
                    { renderTools() }
                    { renderLanguages() }
                  </Grid>
                </div>
              </Grid>
            </Grid>
            { renderActions() }
            { renderExtraEffects() }
            { renderPassives() }
            { renderNotes() }
          </Grid>
        </Grid>
      </div>
    ); 
  }
}
